import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Hammer, RefreshCcw, CheckCircle2, Inbox } from "lucide-react";
import api from "../../lib/api";
import { StatusBadge } from "../../components/StatusTools";

export default function PlannerDashboard() {
  const [cases, setCases] = useState(null);

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api is a stable module import
  }, []);

  const all = cases || [];
  const active = all.filter((c) => !["completed", "final_delivered"].includes(c.status));
  const revisions = all.filter((c) => c.status === "revision_requested");
  const delivered = all.filter((c) => ["completed", "final_delivered"].includes(c.status));

  const cards = [
    { icon: Briefcase, label: "Assigned to me", value: all.length },
    { icon: Hammer, label: "In progress", value: active.length },
    { icon: RefreshCcw, label: "Open revisions", value: revisions.length },
    { icon: CheckCircle2, label: "Delivered", value: delivered.length },
  ];

  return (
    <div data-testid="planner-dashboard">
      <h1 className="font-display font-black text-3xl tracking-tight mb-8">My Case Queue</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`planner-metric-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon className="h-5 w-5 text-sky-600 mb-3" />
            <p className="font-display font-black text-3xl">{cases === null ? "…" : c.value}</p>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{c.label}</p>
          </div>
        ))}
      </div>

      {cases === null ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>
      ) : cases.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-20 text-center" data-testid="planner-empty-state">
          <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="font-display font-bold text-xl">No cases assigned yet</p>
          <p className="text-sm text-slate-500 mt-2">Cases assigned to you by the admin team will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="planner-cases-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Case ID</th>
                  <th className="px-5 py-3.5 font-semibold">Doctor</th>
                  <th className="px-5 py-3.5 font-semibold">Patient Ref</th>
                  <th className="px-5 py-3.5 font-semibold">Type</th>
                  <th className="px-5 py-3.5 font-semibold">Status</th>
                  <th className="px-5 py-3.5 font-semibold">Updated</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`planner-case-row-${c.case_id}`}>
                    <td className="px-5 py-4 font-mono font-semibold text-sky-700">{c.case_id}</td>
                    <td className="px-5 py-4">{c.client_name}</td>
                    <td className="px-5 py-4 text-slate-500">{c.patient_ref}</td>
                    <td className="px-5 py-4 text-slate-500">{c.case_type}</td>
                    <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{new Date(c.updated_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      <Link to={`/planner/cases/${c.id}`} data-testid={`planner-case-open-${c.case_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                        Work case <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
